import _ from 'underscore';
import type {
  EntityKey, EntityObject, FetchPolicyValue, OverreactSpec,
} from '../public-types';
import { FetchPolicy } from '../middleware';
import { responseTypes } from '../spec';
import type { Record as EntityRecord } from '../store/record';

export type LookupCacheFn<TEntity extends EntityObject, TVariables extends object> = (
  _items: TEntity[],
  _variables: TVariables,
) => TEntity | TEntity[] | undefined;

export function getDataFromRecords<TEntity extends EntityObject>(
  records: Array<EntityRecord<TEntity> | undefined>,
  responseContract: { responseType: string },
) {
  const values = records.filter(Boolean).map(record => record?.getData()) as TEntity[];
  return responseContract.responseType === responseTypes.COLL ? values : values[0];
}

function getDefaultLookupCacheFn<TEntity extends EntityObject, TVariables extends object>(
  variableKeySelector: (_variables: TVariables) => EntityKey,
  dataKeySelector: (_entity: TEntity) => EntityKey,
): LookupCacheFn<TEntity, TVariables> {
  return (items, variables) =>
    _.find(items, item => dataKeySelector(item) === variableKeySelector(variables));
}

export function getLookupCacheFn<TEntity extends EntityObject, TVariables extends object>(
  lookupCacheFn: LookupCacheFn<TEntity, TVariables> | undefined,
  spec: OverreactSpec<TVariables, unknown, unknown, TEntity>,
  fetchPolicy?: FetchPolicyValue,
) {
  if (fetchPolicy === FetchPolicy.StoreOrNetwork) {
    if (lookupCacheFn) {
      return lookupCacheFn;
    }
    if (_.isFunction(spec.requestContract.keySelector)
      && _.isFunction(spec.responseContract.keySelector)
      && spec.responseContract.responseType === responseTypes.ENTITY) {
      return getDefaultLookupCacheFn(
        spec.requestContract.keySelector,
        spec.responseContract.keySelector,
      );
    }
  }
  return null;
}
